/* eslint-disable max-len */
import React, {useState, useEffect} from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  Button,
  Spinner
} from "reactstrap";
import LaddaButton, {EXPAND_RIGHT} from "react-ladda";
import PropTypes from "prop-types";

import {preCheckObjectEmpty} from "./ModalArchive";
import {
  FormField,
  SelectFormField,
  CustomFormInput,
  CheckFormField
} from "../../../utils/FormFields";
import Autosuggest from "../../AutosuggestCompliance";
import { dateIsEmpty } from "./../../../utils/Helper";

const checkDocument = doc =>
  doc.PhysicalPersonDocumentTypeId &&
  doc.PhysicalPersonDocumentNumber &&
  doc.PersonDocumentIssueCountryId &&
  (doc.PhysicalPersonDocumentWithoutExpiration ||
    doc.PhysicalPersonDocumentExpirationDate);

const parseDoc = doc => {
  if (doc) {
    return {
      PhysicalPersonDocumentTypeId: doc.PhysicalPersonDocumentTypeId,
      PhysicalPersonDocumentTypeName: doc.PhysicalPersonDocumentTypeName,
      PhysicalPersonDocumentNumber: doc.PhysicalPersonDocumentNumber,
      PhysicalPersonDocumentExpirationDate:
        doc.PhysicalPersonDocumentExpirationDate,
      PersonDocumentIssueCountryId: doc.PersonDocumentIssueCountryId,
      PersonDocumentIssueCountryName: doc.PersonDocumentIssueCountryName,
      PhysicalPersonDocumentIsMain: doc.PhysicalPersonDocumentIsMain,
      PhysicalPersonDocumentWithoutExpiration:
        doc.PhysicalPersonDocumentWithoutExpiration
    };
  }
  return undefined;
};

const ModalDocument = ({
  open,
  onToggle,
  onSave,
  documentTypes,
  loading,
  initial,
  error,
  services
}) => {
  const [documentCountry, setDocumentCountry] = useState(
    initial ? initial.PersonDocumentIssueCountryName : ""
  );
  const [newDocument, setNewDocument] = useState(
    parseDoc(initial) || {
      PhysicalPersonDocumentTypeId: "",
      PhysicalPersonDocumentTypeName: "",
      PhysicalPersonDocumentNumber: "",
      PhysicalPersonDocumentWithoutExpiration: false,
      PhysicalPersonDocumentExpirationDate: undefined,
      PersonDocumentIssueCountryId: "",
      PersonDocumentIssueCountryName: "",
      PhysicalPersonDocumentIsMain: false
    }
  );

  useEffect(() => {
    if (documentTypes && !initial) {
      setNewDocument(d => ({
        ...d,
        PhysicalPersonDocumentTypeId: documentTypes[0].DocumentTypeId,
        PhysicalPersonDocumentTypeName: documentTypes[0].DocumentTypeName
      }));
    }
  }, [documentTypes, initial]);

  const onChange = (name, value, countryName) => {
    const newNewDocument = {
      ...newDocument,
      [name]: value
    };
    if (name === "PersonDocumentIssueCountryId") {
      newNewDocument.PersonDocumentIssueCountryName = countryName;
    }
    if (name === "PhysicalPersonDocumentTypeId") {
      documentTypes.map(d => {
        if (d.DocumentTypeId === value) {
          newNewDocument.PhysicalPersonDocumentTypeName = d.DocumentTypeName; 
        }
        return {};
      });
    }
    if (name === "PhysicalPersonDocumentWithoutExpiration") {
      newDocument.PhysicalPersonDocumentExpirationDate = undefined;
    }
    setNewDocument({
      ...newNewDocument
    });
  };

  const onChangeDocumentCountry = name => setDocumentCountry(name);

  const fetchCountrySuggestion = name =>
    new Promise((resolve, reject) => {
      services.fetchAutosuggestCoutries({text: name}, response => {
        if (response && response.countries) {
          const suggestions = [];
          response.countries.map(c => {
            suggestions.push({
              id: c.Id,
              name: c.Name
            });
            return {};
          });
          resolve(suggestions);
        } else {
          reject();
        }
      });
    });

  const handleChangeDocumentCountry = (id, value) => {
    onChange("PersonDocumentIssueCountryId", id, value);
    setDocumentCountry(value);
  };

  return (
    <div>
      <Modal isOpen={open} toggle={() => onToggle()}>
        <ModalHeader toggle={onToggle}>
          {initial === undefined ? "Append New " : "Edit "}
          Document
        </ModalHeader>
        <ModalBody>
          {documentTypes ? (
            <Form>
              <SelectFormField
                disabled={initial !== undefined}
                label="Document Type"
                value={newDocument.PhysicalPersonDocumentTypeId}
                onChange={onChange}
                name="PhysicalPersonDocumentTypeId"
                selectItems={documentTypes}
                getSelectItemId={doc => doc.DocumentTypeId}
                getSelectItemName={doc => doc.DocumentTypeName}
              />
              <CustomFormInput label="Country of Issue">
                <Autosuggest
                  value={documentCountry}
                  onChange={onChangeDocumentCountry}
                  fecthSuggestions={fetchCountrySuggestion}
                  onSelect={handleChangeDocumentCountry}
                />
              </CustomFormInput>
              <FormField
                label="Document Number"
                value={newDocument.PhysicalPersonDocumentNumber}
                name="PhysicalPersonDocumentNumber"
                onChange={onChange}
                error={error}
              />
              <CheckFormField
                label="No Expiration"
                value={newDocument.PhysicalPersonDocumentWithoutExpiration}
                initialValue={
                  newDocument.PhysicalPersonDocumentWithoutExpiration
                }
                name="PhysicalPersonDocumentWithoutExpiration"
                onChange={onChange}
              />
              {!newDocument.PhysicalPersonDocumentWithoutExpiration && 
                <FormField
                  label="Expiration Date"
                  type="date"
                  disabled={newDocument.PhysicalPersonDocumentWithoutExpiration}
                  value={parseDate(newDocument.PhysicalPersonDocumentExpirationDate)}
                  name="PhysicalPersonDocumentExpirationDate"
                  onChange={onChange}
             />
              }
             
              <CheckFormField
                label="Is Main"
                value={newDocument.PhysicalPersonDocumentIsMain}
                initialValue={newDocument.PhysicalPersonDocumentIsMain}
                name="PhysicalPersonDocumentIsMain"
                description="If marked, this Document will be considered the main Document for this Person."
                onChange={onChange}
              />
            </Form>
          ) : (
            <Spinner />
          )}
        </ModalBody>
        <ModalFooter>
          <LaddaButton
            className="btn btn-ladda btn-primary"
            loading={loading}
            disabled={!checkDocument(newDocument)}
            onClick={() => onSave(newDocument)}
            data-style={EXPAND_RIGHT}
          >
            {loading ? "Saving" : "Save"}
          </LaddaButton>{" "}
          <Button color="secondary" onClick={() => onToggle()}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

function parseDate(date) {
  if (date instanceof Date) {
    return date.toISOString();
  }

  if (!date || date == "" || date == "0000-00-00" || dateIsEmpty(date)) {
    return "";
  }
  
  if (date.indexOf("T") > -1) {
    return date;
  }
 

  return new Date(`${date}T00:00`).toISOString();
}

ModalDocument.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  documentTypes: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  initial: PropTypes.object,
  error: PropTypes.string,
  services: PropTypes.object.isRequired
};

ModalDocument.defaultProps = {
  initial: undefined,
  error: undefined
};

export default ModalDocument;
